import React, { useState, useEffect } from 'react';
import {
  Typography,
  Box,
  Paper,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Grid,
  FormControlLabel,
  Checkbox,
  Divider,
  Alert,
  Card,
  CardContent,
  Accordion,
  AccordionSummary,
  AccordionDetails
} from '@mui/material';
import {
  Save as SaveIcon,
  Refresh as RefreshIcon,
  ExpandMore as ExpandMoreIcon
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import Layout from '../components/layout/Layout';
import privilegeService, { getRolePrivileges, saveRolePrivileges } from '../services/privilegeService';

// Menu structure with submenus - use the same structure as in privilegeService
const menuStructure = [
  {
    id: 'DASHBOARD',
    text: 'Dashboard',
    path: '/dashboard',
    submenus: []
  },
  {
    id: 'INVENTORY',
    text: 'Inventory',
    path: '/inventory',
    submenus: [
      { id: 'INVENTORY_CATEGORIES', text: 'Categories', path: '/inventory/categories' },
      { id: 'INVENTORY_PRODUCTS', text: 'Products', path: '/inventory/products' },
      { id: 'INVENTORY_IMPORT_PRODUCTS', text: 'Import Products', path: '/inventory/import-products' },
      { id: 'INVENTORY_STOCK_TRANSFER', text: 'Stock Transfer', path: '/inventory/stock-transfer' },
      { id: 'INVENTORY_TAXES', text: 'Taxes', path: '/inventory/taxes' },
      { id: 'INVENTORY_UNITS', text: 'Units', path: '/inventory/units' }
    ]
  },
  {
    id: 'LOCATION_MANAGEMENT',
    text: 'Location Management',
    path: '/location-management',
    submenus: [
      { id: 'LOCATION_MANAGEMENT_LOCATIONS', text: 'Locations', path: '/inventory/locations' },
      { id: 'LOCATION_MANAGEMENT_USERS', text: 'Location Users', path: '/inventory/location-users' },
      { id: 'LOCATION_MANAGEMENT_STOCK_TRANSFER', text: 'Stock Transfer', path: '/inventory/stock-transfer' }
    ]
  },
  {
    id: 'SALES',
    text: 'Sales',
    path: '/sales',
    submenus: [
      { id: 'SALES_ORDERS', text: 'Add Order', path: '/sales/pos' },
      { id: 'SALES_VIEW_ORDERS', text: 'View Orders', path: '/sales/view-orders' },
      { id: 'SALES_RETURNS', text: 'Returns', path: '/sales/returns' },
      { id: 'SALES_VIEW_RETURNS', text: 'View Returns', path: '/sales/view-returns' }
    ]
  },
  {
    id: 'PURCHASES',
    text: 'Purchases',
    path: '/purchases',
    submenus: [
      { id: 'PURCHASES_ORDERS', text: 'Orders', path: '/purchases/orders' },
      { id: 'PURCHASES_VIEW_ORDERS', text: 'View Orders', path: '/purchases/view-orders' },
      { id: 'PURCHASES_RETURNS', text: 'Returns', path: '/purchases/returns' }
    ]
  },
  {
    id: 'EXPENSES',
    text: 'Expense Management',
    path: '/expenses',
    submenus: [
      { id: 'EXPENSES_ENTRY', text: 'Expense Entry', path: '/expenses/entry' }
    ]
  },
  {
    id: 'PEOPLE',
    text: 'People',
    path: '/people',
    submenus: [
      { id: 'PEOPLE_CUSTOMERS', text: 'Customers', path: '/people/customers' },
      { id: 'PEOPLE_SUPPLIERS', text: 'Suppliers', path: '/people/suppliers' },
      { id: 'PEOPLE_USERS', text: 'Users', path: '/people/users' }
    ]
  },
  {
    id: 'REPORTS',
    text: 'Reports',
    path: '/reports',
    submenus: [
      { id: 'REPORTS_SALES', text: 'Sales Reports', path: '/reports/sales' },
      { id: 'REPORTS_LOCATION_SALES', text: 'Location Sales Reports', path: '/reports/location-sales' },
      { id: 'REPORTS_TOP_SELLING_PRODUCTS', text: 'Top Selling Products', path: '/reports/top-selling-products' },
      { id: 'REPORTS_PURCHASES', text: 'Purchase Reports', path: '/reports/purchases' },
      { id: 'REPORTS_INVENTORY', text: 'Inventory Reports', path: '/reports/inventory' },
      { id: 'REPORTS_STOCK', text: 'Stock Report', path: '/reports/stock' },
      { id: 'REPORTS_DAY_CLOSE', text: 'Day Close Report', path: '/reports/day-close' },
      { id: 'REPORTS_STOCK_TRANSFER', text: 'Stock Transfer Report', path: '/reports/stock-transfer' },
      { id: 'REPORTS_BILLING', text: 'Billing Reports', path: '/reports/billing' },
      { id: 'REPORTS_PROFIT_LOSS', text: 'Profit and Loss Reports', path: '/reports/profit-loss' },
      { id: 'REPORTS_CREDIT_CUSTOMERS', text: 'Manage Credit Customers', path: '/reports/credit-customers' },
      { id: 'REPORTS_GST', text: 'GSTR1', path: '/reports/gst' },
      { id: 'REPORTS_GSTR2', text: 'GSTR2', path: '/reports/gstr2' },
      { id: 'REPORTS_GSTR3B', text: 'GSTR-3B', path: '/reports/gstr3b' },
      { id: 'REPORTS_HSN', text: 'HSN Wise Reports', path: '/reports/hsn' }
    ]
  },
  {
    id: 'PRIVILEGES',
    text: 'Privileges',
    path: '/privileges',
    submenus: []
  },
  {
    id: 'SETTINGS',
    text: 'Settings',
    path: '/settings',
    submenus: [
      { id: 'SETTINGS_COMPANY', text: 'Company Settings', path: '/settings/company' },
      { id: 'SETTINGS_PRINT', text: 'Print Settings', path: '/settings/print' },
      { id: 'SETTINGS_WHATSAPP', text: 'WhatsApp Configuration', path: '/settings/whatsapp' }
    ]
  }
];

const PrivilegesPage = () => {
  const { enqueueSnackbar } = useSnackbar();
  const [roles] = useState(Object.keys(privilegeService.ROLES));
  const [selectedRole, setSelectedRole] = useState('');
  const [privileges, setPrivileges] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saveLoading, setSaveLoading] = useState(false);

  useEffect(() => {
    if (selectedRole) {
      fetchRolePrivileges(selectedRole);
    }
  }, [selectedRole]);

  const fetchRolePrivileges = async (role) => {
    try {
      setLoading(true);

      // Get role privileges from the service
      const { visibleMenus = [], accessibleRoutes = [] } = getRolePrivileges(role) || {};
      console.log('Fetched privileges for role:', role, { visibleMenus, accessibleRoutes });

      // Convert to the format expected by the UI
      const menuPrivileges = convertPrivilegesToUIFormat(visibleMenus, accessibleRoutes);
      setPrivileges(menuPrivileges);

      setLoading(false);
    } catch (error) {
      console.error('Error fetching role privileges:', error);
      enqueueSnackbar('Failed to load role privileges', { variant: 'error' });
      setLoading(false);
    }
  };

  // Convert from the service format to the UI format
  const convertPrivilegesToUIFormat = (visibleMenus = [], accessibleRoutes = []) => {
    return menuStructure.map(menu => {
      // Main menu permissions
      const menuPermission = {
        id: menu.id,
        text: menu.text,
        path: menu.path,
        canView: visibleMenus.includes(menu.id),
        canCreate: visibleMenus.includes(menu.id),
        canEdit: visibleMenus.includes(menu.id),
        canDelete: visibleMenus.includes(menu.id),
        submenus: []
      };

      // Submenu permissions
      menuPermission.submenus = menu.submenus.map(submenu => {
        return {
          id: submenu.id,
          text: submenu.text,
          path: submenu.path,
          canView: visibleMenus.includes(submenu.id),
          canCreate: visibleMenus.includes(submenu.id),
          canEdit: visibleMenus.includes(submenu.id),
          canDelete: visibleMenus.includes(submenu.id)
        };
      });

      return menuPermission;
    });
  };

  // Convert from UI format back to service format
  const convertUIToServiceFormat = (privileges) => {
    const visibleMenus = [];
    const accessibleRoutes = [];

    privileges.forEach(menu => {
      if (menu.canView) {
        // Add main menu to visible menus
        visibleMenus.push(menu.id);

        // If the menu has a path, add it to accessible routes
        if (menu.path) {
          accessibleRoutes.push(menu.path);

          // For routes with nested paths, add wildcard permissions
          if (['SALES', 'PURCHASES', 'REPORTS', 'PEOPLE', 'INVENTORY'].includes(menu.id)) {
            accessibleRoutes.push(`${menu.path}/*`);
          }
        }

        // Process submenus
        menu.submenus.forEach(submenu => {
          if (submenu.canView) {
            // Add submenu ID to visible menus
            visibleMenus.push(submenu.id);

            // Add submenu path to accessible routes
            if (submenu.path) {
              accessibleRoutes.push(submenu.path);

              // Extract the parent path segment
              const pathParts = submenu.path.split('/');
              if (pathParts.length >= 2) {
                const parentPath = `/${pathParts[1]}`;

                // Add the parent path if it's not already in the list
                if (!accessibleRoutes.includes(parentPath)) {
                  accessibleRoutes.push(parentPath);
                }

                // Add wildcard for the parent path
                const wildcardPath = `${parentPath}/*`;
                if (!accessibleRoutes.includes(wildcardPath)) {
                  accessibleRoutes.push(wildcardPath);
                }
              }
            }
          }
        });
      }
    });

    // Add dashboard by default for all roles
    if (!accessibleRoutes.includes('/dashboard')) {
      accessibleRoutes.push('/dashboard');
      if (!visibleMenus.includes('DASHBOARD')) {
        visibleMenus.push('DASHBOARD');
      }
    }

    return { visibleMenus, accessibleRoutes };
  };

  const handleRoleChange = (event) => {
    setSelectedRole(event.target.value);
  };

  const handlePrivilegeChange = (menuIndex, field, submenuIndex = -1) => {
    setPrivileges(prevPrivileges => {
      const newPrivileges = [...prevPrivileges];

      if (submenuIndex === -1) {
        // Main menu privilege change
        newPrivileges[menuIndex] = {
          ...newPrivileges[menuIndex],
          [field]: !newPrivileges[menuIndex][field]
        };

        // If main menu view is unchecked, uncheck all submenu views too
        if (field === 'canView' && !newPrivileges[menuIndex][field]) {
          newPrivileges[menuIndex].submenus.forEach((submenu, idx) => {
            newPrivileges[menuIndex].submenus[idx].canView = false;
          });
        }
      } else {
        // Submenu privilege change
        newPrivileges[menuIndex].submenus[submenuIndex] = {
          ...newPrivileges[menuIndex].submenus[submenuIndex],
          [field]: !newPrivileges[menuIndex].submenus[submenuIndex][field]
        };

        // If a submenu view is checked, ensure parent menu view is checked
        if (field === 'canView' && newPrivileges[menuIndex].submenus[submenuIndex][field]) {
          newPrivileges[menuIndex].canView = true;
        }
      }

      return newPrivileges;
    });
  };

  const handleSavePrivileges = async () => {
    if (!selectedRole) {
      enqueueSnackbar('Please select a role', { variant: 'error' });
      return;
    }

    try {
      setSaveLoading(true);

      // Convert UI format to service format
      const serviceFormat = convertUIToServiceFormat(privileges);
      console.log('Saving privileges for role:', selectedRole, serviceFormat);

      // Save using the privilege service for frontend localStorage
      const success = saveRolePrivileges(selectedRole, serviceFormat);

      if (success) {
        // Add a note about backend synchronization
        enqueueSnackbar('Role privileges saved successfully in frontend. Note: These privileges are currently only stored locally. Backend users with this role will need separate configuration.', { variant: 'success' });
      } else {
        throw new Error('Failed to save privileges');
      }

      setSaveLoading(false);
    } catch (error) {
      console.error('Error saving role privileges:', error);
      enqueueSnackbar('Failed to save role privileges', { variant: 'error' });
      setSaveLoading(false);
    }
  };

  const handleRefresh = () => {
    if (selectedRole) {
      fetchRolePrivileges(selectedRole);
    }
  };

  const getUserRoleLabel = (role) => {
    switch (role) {
      case 'ADMIN':
        return 'Admin';
      case 'MANAGER':
        return 'Manager';
      case 'EXECUTIVE':
        return 'Executive';
      case 'BILLING_PERSON':
        return 'Billing Person';
      case 'USER':
        return 'Regular User';
      default:
        return role;
    }
  };

  return (
    <Layout>
      <Box sx={{ p: 3 }}>
        <Typography variant="h4" gutterBottom>
          Role Privileges Management
        </Typography>

        <Paper sx={{ p: 3, mb: 3 }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Select Role</InputLabel>
                <Select
                  value={selectedRole}
                  onChange={handleRoleChange}
                  label="Select Role"
                  disabled={loading}
                >
                  <MenuItem value="" disabled>
                    -- Select a role --
                  </MenuItem>
                  {roles.map((role) => (
                    <MenuItem key={role} value={role}>
                      {getUserRoleLabel(role)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6} sx={{ display: 'flex', justifyContent: 'flex-end' }}>
              <Button
                variant="outlined"
                startIcon={<RefreshIcon />}
                onClick={handleRefresh}
                sx={{ mr: 1 }}
                disabled={!selectedRole || loading}
              >
                Refresh
              </Button>
              <Button
                variant="contained"
                startIcon={<SaveIcon />}
                onClick={handleSavePrivileges}
                disabled={!selectedRole || loading || saveLoading}
              >
                {saveLoading ? <CircularProgress size={24} /> : 'Save Privileges'}
              </Button>
            </Grid>
          </Grid>
        </Paper>

        {!selectedRole ? (
          <Alert severity="info" sx={{ mt: 2 }}>
            Please select a role to manage privileges
          </Alert>
        ) : loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Privilege Legend
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={6} md={3}>
                    <FormControlLabel
                      control={<Checkbox checked={true} />}
                      label="View: Access to see the page/data"
                    />
                  </Grid>
                  <Grid item xs={6} md={3}>
                    <FormControlLabel
                      control={<Checkbox checked={true} />}
                      label="Create: Permission to add new items"
                    />
                  </Grid>
                  <Grid item xs={6} md={3}>
                    <FormControlLabel
                      control={<Checkbox checked={true} />}
                      label="Edit: Permission to modify items"
                    />
                  </Grid>
                  <Grid item xs={6} md={3}>
                    <FormControlLabel
                      control={<Checkbox checked={true} />}
                      label="Delete: Permission to remove items"
                    />
                  </Grid>
                </Grid>
              </CardContent>
            </Card>

            <Alert severity="info" sx={{ mb: 3 }}>
              Configure menu and page visibility for this role. When a user with this role logs in, they will only see the menus and pages that have "View" permission enabled.
            </Alert>

            {privileges.map((menu, menuIndex) => (
              <Accordion key={menu.id} sx={{ mb: 2 }}>
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  sx={{
                    backgroundColor: menu.canView ? 'rgba(0, 128, 0, 0.1)' : 'rgba(255, 0, 0, 0.1)',
                    '&.Mui-expanded': {
                      backgroundColor: menu.canView ? 'rgba(0, 128, 0, 0.1)' : 'rgba(255, 0, 0, 0.1)'
                    }
                  }}
                >
                  <Typography variant="subtitle1" sx={{ fontWeight: 'bold', flex: 1 }}>
                    {menu.text} Menu
                  </Typography>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={menu.canView}
                        onChange={() => handlePrivilegeChange(menuIndex, 'canView')}
                        onClick={(e) => e.stopPropagation()}
                      />
                    }
                    label="View"
                    sx={{ mr: 2 }}
                    onClick={(e) => e.stopPropagation()}
                  />
                </AccordionSummary>
                <AccordionDetails>
                  {menu.submenus.length > 0 ? (
                    <TableContainer component={Paper} variant="outlined">
                      <Table>
                        <TableHead>
                          <TableRow>
                            <TableCell>Submenu</TableCell>
                            <TableCell align="center">View</TableCell>
                            <TableCell align="center">Create</TableCell>
                            <TableCell align="center">Edit</TableCell>
                            <TableCell align="center">Delete</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {menu.submenus.map((submenu, submenuIndex) => (
                            <TableRow key={submenu.id}>
                              <TableCell component="th" scope="row">
                                {submenu.text}
                              </TableCell>
                              <TableCell align="center">
                                <Checkbox
                                  checked={submenu.canView}
                                  onChange={() => handlePrivilegeChange(menuIndex, 'canView', submenuIndex)}
                                  disabled={!menu.canView}
                                />
                              </TableCell>
                              <TableCell align="center">
                                <Checkbox
                                  checked={submenu.canCreate}
                                  onChange={() => handlePrivilegeChange(menuIndex, 'canCreate', submenuIndex)}
                                  disabled={!submenu.canView}
                                />
                              </TableCell>
                              <TableCell align="center">
                                <Checkbox
                                  checked={submenu.canEdit}
                                  onChange={() => handlePrivilegeChange(menuIndex, 'canEdit', submenuIndex)}
                                  disabled={!submenu.canView}
                                />
                              </TableCell>
                              <TableCell align="center">
                                <Checkbox
                                  checked={submenu.canDelete}
                                  onChange={() => handlePrivilegeChange(menuIndex, 'canDelete', submenuIndex)}
                                  disabled={!submenu.canView}
                                />
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  ) : (
                    <TableContainer component={Paper} variant="outlined">
                      <Table>
                        <TableHead>
                          <TableRow>
                            <TableCell>Menu</TableCell>
                            <TableCell align="center">View</TableCell>
                            <TableCell align="center">Create</TableCell>
                            <TableCell align="center">Edit</TableCell>
                            <TableCell align="center">Delete</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          <TableRow>
                            <TableCell component="th" scope="row">
                              {menu.text}
                            </TableCell>
                            <TableCell align="center">
                              <Checkbox
                                checked={menu.canView}
                                onChange={() => handlePrivilegeChange(menuIndex, 'canView')}
                              />
                            </TableCell>
                            <TableCell align="center">
                              <Checkbox
                                checked={menu.canCreate}
                                onChange={() => handlePrivilegeChange(menuIndex, 'canCreate')}
                                disabled={!menu.canView}
                              />
                            </TableCell>
                            <TableCell align="center">
                              <Checkbox
                                checked={menu.canEdit}
                                onChange={() => handlePrivilegeChange(menuIndex, 'canEdit')}
                                disabled={!menu.canView}
                              />
                            </TableCell>
                            <TableCell align="center">
                              <Checkbox
                                checked={menu.canDelete}
                                onChange={() => handlePrivilegeChange(menuIndex, 'canDelete')}
                                disabled={!menu.canView}
                              />
                            </TableCell>
                          </TableRow>
                        </TableBody>
                      </Table>
                    </TableContainer>
                  )}
                </AccordionDetails>
              </Accordion>
            ))}

            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
              <Button
                variant="contained"
                startIcon={<SaveIcon />}
                onClick={handleSavePrivileges}
                disabled={loading || saveLoading}
              >
                {saveLoading ? <CircularProgress size={24} /> : 'Save Privileges'}
              </Button>
            </Box>
          </>
        )}
      </Box>
    </Layout>
  );
};

export default PrivilegesPage; 
